import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

type ConsentStatus = "accepted" | "declined";

const GTAG_ID = "AW-17718965875";
const CONSENT_STORAGE_KEY = "zendizz-cookie-consent";
const DISABLE_KEY = `ga-disable-${GTAG_ID}`;

type StoredConsent = {
  status: ConsentStatus;
  timestamp: number;
};

function ensureGtag() {
  if (typeof window === "undefined") return null;
  const w = window as typeof window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

  if (!w.dataLayer) {
    w.dataLayer = [];
  }

  if (!w.gtag) {
    w.gtag = function gtag(...args: unknown[]) {
      w.dataLayer?.push(args);
    };
  }

  return w.gtag;
}

function setTrackingDisabled(disabled: boolean) {
  if (typeof window === "undefined") return;
  (window as typeof window & Record<string, boolean>)[DISABLE_KEY] = disabled;
}

function updateConsent(status: ConsentStatus) {
  setTrackingDisabled(status !== "accepted");

  const gtag = ensureGtag();
  if (!gtag) {
    return;
  }

  gtag("consent", "update", {
    ad_storage: status === "accepted" ? "granted" : "denied",
    analytics_storage: status === "accepted" ? "granted" : "denied",
    ad_user_data: status === "accepted" ? "granted" : "denied",
    ad_personalization: status === "accepted" ? "granted" : "denied",
  });

  if (status === "accepted") {
    gtag("config", GTAG_ID);
  }
}

function readStoredConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as StoredConsent;
  } catch (error) {
    console.warn("Failed to read cookie consent", error);
    return null;
  }
}

export const CookieConsent: React.FC = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = React.useState(false);
  const [status, setStatus] = React.useState<ConsentStatus | null>(null);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const stored = readStoredConsent();
    if (stored?.status === "accepted" || stored?.status === "declined") {
      setStatus(stored.status);
      updateConsent(stored.status);
      return;
    }

    setTrackingDisabled(true);
    setIsVisible(true);
  }, []);

  const handleDecision = React.useCallback((decision: ConsentStatus) => {
    if (typeof window === "undefined") return;

    const record: StoredConsent = {
      status: decision,
      timestamp: Date.now(),
    };

    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch (error) {
      console.warn("Failed to persist cookie consent", error);
    }

    setStatus(decision);
    setIsVisible(false);
    updateConsent(decision);
  }, []);

  if (!isVisible || status !== null) {
    return null;
  }

  const consentCopy =
    language === "PL"
      ? {
        title: "Twoja prywatność",
        description:
          "Używamy plików cookie do analizy ruchu i poprawy działania strony. Możesz zaakceptować lub odrzucić opcjonalne cookie. Twoja decyzja zostanie zapisana.",
        accept: "Akceptuję",
        decline: "Odrzuć",
        learnMore: "Dowiedz się więcej",
      }
      : {
        title: "Your privacy matters",
        description:
          "We use cookies to analyse traffic and improve the site experience. You can accept or decline optional cookies. Your choice will be saved.",
        accept: "Accept",
        decline: "Decline",
        learnMore: "Learn more",
      };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 animate-fade-in-up">
      <div className="mx-auto max-w-4xl rounded-2xl bg-black/90 backdrop-blur-xl border border-white/10 p-6 shadow-[0_0_30px_rgba(168,85,247,0.15)] hover:border-purple-500/40 transition-all duration-300">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <h3 className="text-lg font-bold bg-gradient-energy bg-clip-text text-transparent">
              {consentCopy.title}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {consentCopy.description}{" "}
              <Link
                to="/privacy"
                className="font-medium text-cyan-400 hover:text-cyan-300 underline-offset-2 hover:underline transition-colors"
              >
                {consentCopy.learnMore}
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-shrink-0">
            <button
              onClick={() => handleDecision("declined")}
              className="relative px-6 py-2.5 rounded-lg transition-all duration-300 font-medium text-sm tracking-wide group bg-white/[0.02] backdrop-blur-sm border border-white/10 hover:border-white/20 hover:bg-white/5 w-full sm:w-auto"
            >
              <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-transparent via-white/5 to-transparent blur-sm"></span>
              <span className="relative z-10">{consentCopy.decline}</span>
            </button>
            <button
              onClick={() => handleDecision("accepted")}
              className="relative px-6 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 text-white hover:opacity-90 hover:scale-105 transition-all duration-300 shadow-[0_0_20px_rgba(6,182,212,0.4)] font-bold text-sm w-full sm:w-auto"
            >
              {consentCopy.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
